(() => {
  if (customElements.get('shinora-chain-studio')) return;

  let instanceCount = 0;
  const categories = ['base', 'charm', 'straight'];
  const escape = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
  const safeUrl = (value) => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (_) {
      return '';
    }
  };

  class ShinoraChainStudio extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      if (!this.querySelector('[data-studio-data]') || !this.querySelector('[data-studio-mount]')) {
        this.observer = new MutationObserver(() => {
          if (this.querySelector('[data-studio-data]') && this.querySelector('[data-studio-mount]')) {
            this.observer.disconnect();
            this.connectedCallback();
          }
        });
        this.observer.observe(this, { childList: true, subtree: true });
        return;
      }
      this.mount = this.querySelector('[data-studio-mount]');
      try {
        if (!this.products) this.initialize();
        this.listeners = new AbortController();
        const options = { signal: this.listeners.signal };
        this.mount.addEventListener('click', (event) => this.handleClick(event), options);
        this.mount.addEventListener('change', (event) => this.handleChange(event), options);
        this.dialog?.addEventListener('close', () => {
          this.modalProduct = null;
          if (this.modalTrigger?.isConnected) this.modalTrigger.focus({ preventScroll: true });
        }, options);
      } catch (error) {
        this.setStatus(this.text('error'), true);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.listeners?.abort();
      this.listeners = null;
      if (this.dialog?.open) this.dialog.close();
    }

    initialize() {
      this.data = JSON.parse(this.querySelector('[data-studio-data]').textContent);
      this.studioPrefix = `scs-${++instanceCount}`;
      this.products = new Map();
      for (const raw of this.data.products || []) {
        const id = Number(raw.id);
        if (!Number.isSafeInteger(id) || !categories.includes(raw.category)) continue;
        const variants = (raw.variants || []).map((variant) => ({ ...variant, id: Number(variant.id), price: Number(variant.price) }))
          .filter((variant) => Number.isSafeInteger(variant.id) && Number.isFinite(variant.price) && variant.price >= 0);
        this.products.set(id, { ...raw, id, variants });
      }
      const bases = this.inCategory('base');
      const preferred = this.products.get(Number(this.data.defaultProductId));
      const base = preferred?.category === 'base' && this.isAvailable(preferred) ? preferred : bases.find((product) => this.isAvailable(product));
      this.selected = { base: base?.id ?? null, charm: new Set(), straight: new Set() };
      this.variantSelections = new Map();
      if (base?.variants.length === 1) this.variantSelections.set(base.id, base.variants[0].id);
      this.galleryIndex = 0;
      this.pending = false;
      const currency = this.data.currency || window.Shopify?.currency?.active || 'USD';
      try {
        this.formatter = new Intl.NumberFormat(this.data.locale || document.documentElement.lang || 'en', { style: 'currency', currency });
      } catch (_) {
        this.formatter = new Intl.NumberFormat('en', { style: 'currency', currency });
      }
      this.render();
    }

    text(key) {
      const defaults = { choose: 'Choose', selected: 'Selected', details: 'Details', noCharm: 'No charm', noStraight: 'No straight section', add: 'Add to cart', adding: 'Adding…', added: 'Added to your cart.', soldOut: 'Sold out', selectVariant: 'Choose an option', error: 'We could not confirm your cart update. Please check your cart before trying again.', gallery: 'View product photos', close: 'Close', subtotal: 'Subtotal', item: 'item', items: 'items', viewCart: 'View cart', loading: 'Loading…', base: 'Phone chain', charm: 'Phone charm', straight: 'Straight section', previous: 'Previous photo', next: 'Next photo', priceFrom: 'From', variant: 'Option', empty: 'No phone chains are currently available.', noImage: 'Photo unavailable' };
      return this.data?.strings?.[key] || defaults[key] || key;
    }

    money(price) { return this.formatter.format(price / 100); }
    inCategory(category) { return [...this.products.values()].filter((product) => product.category === category); }
    isAvailable(product) { return Boolean(product?.available && product.variants.some((variant) => variant.available)); }
    isSelected(product) { return product.category === 'base' ? this.selected.base === product.id : this.selected[product.category].has(product.id); }
    selectedProducts(category) {
      const ids = category === 'base' ? [this.selected.base] : [...this.selected[category]];
      return ids.map((id) => this.products.get(id)).filter(Boolean);
    }
    variant(product) { return product?.variants.find((variant) => variant.id === this.variantSelections.get(product.id)); }
    photos(product) { return product?.images?.filter((image) => safeUrl(image.src))?.length ? product.images.filter((image) => safeUrl(image.src)) : (safeUrl(product?.cardImage?.src) ? [product.cardImage] : []); }

    priceIsOnSale(product, variant = this.variant(product)) {
      const candidates = variant ? [variant] : (product?.variants || []);
      const minimum = Math.min(...candidates.map((item) => item.price));
      const lowest = candidates.filter((item) => item.price === minimum);
      return lowest.length > 0 && lowest.every((item) => item.compare_at_price > item.price);
    }

    productPrice(product) {
      if (!product?.variants.length) return this.text('soldOut');
      const chosen = this.variant(product);
      if (chosen) return this.money(chosen.price);
      const prices = product.variants.map((variant) => variant.price);
      const minimum = Math.min(...prices);
      return `${Math.max(...prices) !== minimum ? `${this.text('priceFrom')} ` : ''}${this.money(minimum)}`;
    }

    imageMarkup(image, title, eager = false) {
      const src = safeUrl(image?.src);
      if (!src) return `<span class="scs-empty">${escape(this.text('noImage'))}</span>`;
      const dimensions = Number(image.width) > 0 && Number(image.height) > 0 ? ` width="${Number(image.width)}" height="${Number(image.height)}"` : '';
      return `<img src="${escape(src)}" alt="${escape(image.alt || title)}"${dimensions} loading="${eager ? 'eager' : 'lazy'}" decoding="async">`;
    }

    cardMarkup(product) {
      const available = this.isAvailable(product);
      const editorAttributes = window.Shopify?.designMode && typeof product.editorAttributes === 'string' ? product.editorAttributes : '';
      return `<article class="scs-card" data-product-card="${product.id}" data-sold-out="${!available}" ${editorAttributes}>
        <button type="button" class="scs-choice" data-action="choose" data-product-id="${product.id}" aria-pressed="false" aria-label="${escape(`${this.text('choose')} ${product.title}`)}"${available ? '' : ' disabled'}>
          ${this.imageMarkup(product.cardImage || this.photos(product)[0], product.title)}
          <span class="scs-card-name">${escape(product.label || product.title)}</span>
          <span class="scs-card-price${available && this.priceIsOnSale(product) ? ' scs-price--sale' : ''}">${escape(available ? this.productPrice(product) : this.text('soldOut'))}</span>
          <span class="scs-selected-mark" hidden>${escape(this.text('selected'))}</span>
        </button>
        <button type="button" class="scs-details-button" data-action="details" data-product-id="${product.id}" aria-label="${escape(`${this.text('details')}: ${product.title}`)}">${escape(this.text('details'))}</button>
      </article>`;
    }

    render() {
      categories.forEach((category, index) => {
        const wrapper = this.mount.querySelector(`[data-picker-category="${category}"]`);
        const picker = wrapper?.querySelector('[data-picker-mount]');
        if (!picker) return;
        wrapper.setAttribute('aria-labelledby', `${this.studioPrefix}-${category}`);
        picker.innerHTML = `
        <h3 class="scs-step-heading" id="${this.studioPrefix}-${category}">${index + 1}. ${escape(this.text(category))}</h3>
        <div class="scs-grid scs-${category === 'base' ? 'bases' : category === 'charm' ? 'charms' : 'straights'}" role="group" aria-labelledby="${this.studioPrefix}-${category}">
          ${category === 'base' ? '' : `<button type="button" class="scs-choice scs-no-choice" data-action="clear" data-category="${category}" aria-pressed="true">${escape(this.text(category === 'charm' ? 'noCharm' : 'noStraight'))}</button>`}
          ${this.inCategory(category).map((product) => this.cardMarkup(product)).join('')}
        </div>
        <div class="scs-selection" data-selection="${category}"></div>
        `;
      });
      this.dialog = this.mount.querySelector('dialog');
      this.dialog?.setAttribute('aria-labelledby', `${this.studioPrefix}-modal-title`);
      this.renderMain();
      this.updateSelections();
    }

    setRichText(target, html) {
      if (!target) return;
      const template = document.createElement('template');
      template.innerHTML = html || '';
      template.content.querySelectorAll('script, object, embed').forEach((node) => node.remove());
      template.content.querySelectorAll('*').forEach((node) => {
        [...node.attributes].forEach((attribute) => {
          if (/^on/i.test(attribute.name) || (['href', 'src', 'action', 'xlink:href'].includes(attribute.name) && /^\s*javascript:/i.test(attribute.value))) node.removeAttribute(attribute.name);
        });
      });
      target.replaceChildren(template.content);
    }

    renderMain() {
      const product = this.products.get(this.selected.base);
      const title = this.mount.querySelector('[data-main-title]');
      const price = this.mount.querySelector('[data-main-price]');
      if (title) title.textContent = this.data.titleOverride || product?.title || this.text('empty');
      if (price) {
        price.textContent = product ? this.productPrice(product) : '';
        price.classList.toggle('scs-price--sale', this.priceIsOnSale(product));
      }
      this.setRichText(this.mount.querySelector('[data-main-copy]'), this.data.descriptionOverride || product?.description);
      this.renderGallery();
    }

    renderGallery() {
      const stage = this.mount.querySelector('[data-main-stage]');
      const controls = this.mount.querySelector('[data-main-controls]');
      if (!stage) return;
      const product = this.products.get(this.selected.base);
      const images = this.photos(product);
      this.galleryIndex = images.length ? Math.min(this.galleryIndex, images.length - 1) : 0;
      const currentImage = images[this.galleryIndex];
      stage.innerHTML = `<button type="button" class="scs-stage" data-action="gallery-open" aria-label="${escape(this.text('gallery'))}"${images.length ? '' : ' disabled'}>${this.imageMarkup(currentImage, product?.title || '', true)}</button>`;
      if (controls) controls.innerHTML = images.length > 1 ? `<button type="button" data-action="gallery-prev" aria-label="${escape(this.text('previous'))}">←</button><span>${this.galleryIndex + 1} / ${images.length}</span><button type="button" data-action="gallery-next" aria-label="${escape(this.text('next'))}">→</button><div class="scs-thumbs">${images.map((image, index) => `<button type="button" class="scs-thumb" data-action="gallery-index" data-index="${index}" aria-label="${escape(`${this.text('gallery')} ${index + 1}`)}" aria-pressed="${index === this.galleryIndex}">${this.imageMarkup(image, product.title)}</button>`).join('')}</div>` : '';
    }

    variantControl(product, selectedId, context) {
      if (product.variants.length <= 1) return '';
      const id = `${this.studioPrefix}-${context}-variant-${product.id}`;
      return `<label class="scs-variant-label" for="${id}">${escape(this.text('variant'))}</label><select class="scs-variant" id="${id}" data-variant-product="${product.id}" data-variant-context="${context}"${this.pending ? ' disabled' : ''} required><option value="">${escape(this.text('selectVariant'))}</option>${product.variants.map((variant) => `<option value="${variant.id}"${variant.id === selectedId ? ' selected' : ''}${variant.available ? '' : ' disabled'}>${escape(`${variant.title} — ${this.money(variant.price)}${variant.available ? '' : ` — ${this.text('soldOut')}`}`)}</option>`).join('')}</select>`;
    }

    updateSelections() {
      const focused = this.mount.contains(document.activeElement) ? document.activeElement?.id : null;
      this.mount.querySelectorAll('[data-product-card]').forEach((card) => {
        const product = this.products.get(Number(card.dataset.productCard));
        if (!product) return;
        const cardPrice = card.querySelector('.scs-card-price');
        if (cardPrice) {
          cardPrice.textContent = this.isAvailable(product) ? this.productPrice(product) : this.text('soldOut');
          cardPrice.classList.toggle('scs-price--sale', this.isAvailable(product) && this.priceIsOnSale(product));
        }
        const selected = this.isSelected(product);
        card.dataset.selected = String(selected);
        card.classList.toggle('is-selected', selected);
        const choice = card.querySelector('.scs-choice');
        if (!choice) return;
        choice.setAttribute('aria-pressed', String(selected));
        choice.disabled = this.pending || !this.isAvailable(product);
        const mark = card.querySelector('.scs-selected-mark');
        if (mark) mark.hidden = !selected;
      });
      this.mount.querySelectorAll('[data-action="clear"]').forEach((button) => {
        button.setAttribute('aria-pressed', String(this.selected[button.dataset.category].size === 0));
        button.disabled = this.pending;
      });
      categories.forEach((category) => {
        const products = this.selectedProducts(category);
        const selection = this.mount.querySelector(`[data-selection="${category}"]`);
        if (selection) selection.innerHTML = products.map((product) => {
          const title = `<span>${escape(product.title)}</span>`;
          return `${category === 'base' ? title : `<div>${title}</div>`}${this.variantControl(product, this.variantSelections.get(product.id), category)}`;
        }).join('');
      });
      const selectedProducts = categories.flatMap((category) => this.selectedProducts(category));
      const ready = Boolean(this.selected.base) && selectedProducts.every((product) => this.isAvailable(product) && this.variant(product)?.available);
      const subtotal = this.mount.querySelector('[data-subtotal]');
      const itemCount = this.mount.querySelector('[data-item-count]');
      if (subtotal) subtotal.textContent = ready ? this.money(selectedProducts.reduce((sum, product) => sum + this.variant(product).price, 0)) : (this.selected.base ? this.text('selectVariant') : this.text('soldOut'));
      if (itemCount) itemCount.textContent = `${selectedProducts.length} ${this.text(selectedProducts.length === 1 ? 'item' : 'items')}`;
      const add = this.mount.querySelector('[data-action="add"]');
      if (add) {
        add.textContent = this.text(this.pending ? 'adding' : 'add');
        add.disabled = this.pending || !ready;
        add.setAttribute('aria-busy', String(this.pending));
      }
      const base = this.products.get(this.selected.base);
      const price = this.mount.querySelector('[data-main-price]');
      if (price) {
        price.textContent = base ? this.productPrice(base) : '';
        price.classList.toggle('scs-price--sale', this.priceIsOnSale(base));
      }
      if (focused) this.mount.querySelector(`#${CSS.escape(focused)}`)?.focus({ preventScroll: true });
    }

    selectProduct(product, variantId, toggle = false) {
      if (this.pending || !this.isAvailable(product)) return;
      const baseChanged = product.category === 'base' && this.selected.base !== product.id;
      if (product.category === 'base') this.selected.base = product.id;
      else if (toggle && this.isSelected(product)) this.selected[product.category].delete(product.id);
      else this.selected[product.category].add(product.id);
      if (variantId && product.variants.some((variant) => variant.id === variantId && variant.available)) this.variantSelections.set(product.id, variantId);
      else if (product.variants.length === 1) this.variantSelections.set(product.id, product.variants[0].id);
      if (baseChanged) {
        this.galleryIndex = 0;
        this.renderMain();
      }
      this.setStatus('');
      this.updateSelections();
    }

    handleChange(event) {
      const select = event.target.closest('[data-variant-product]');
      if (!select || this.pending) return;
      const product = this.products.get(Number(select.dataset.variantProduct));
      const id = Number(select.value) || null;
      if (id && !product?.variants.some((variant) => variant.id === id && variant.available)) return;
      if (select.dataset.variantContext === 'modal') {
        this.modalVariantId = id;
        this.updateModalButton();
      } else {
        if (id) this.variantSelections.set(product.id, id);
        else this.variantSelections.delete(product.id);
        this.setStatus('');
        this.updateSelections();
      }
    }

    handleClick(event) {
      if (event.target === this.dialog && this.dialog?.open) {
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }
      const button = event.target.closest('[data-action]');
      if (!button || !this.mount.contains(button) || button.disabled) return;
      const action = button.dataset.action;
      const product = this.products.get(Number(button.dataset.productId));
      if (action === 'choose') this.selectProduct(product, null, true);
      else if (action === 'clear' && !this.pending && ['charm', 'straight'].includes(button.dataset.category)) {
        this.selected[button.dataset.category].clear();
        this.setStatus('');
        this.updateSelections();
      } else if (action === 'details') this.openDetails(product, button);
      else if (action === 'gallery-open') this.openDetails(this.products.get(this.selected.base), button, this.galleryIndex);
      else if (action === 'close') this.dialog.close();
      else if (action === 'modal-choose' && !this.pending && this.modalProduct) {
        if (this.modalProduct.variants.length > 1 && !this.modalVariantId) return;
        this.selectProduct(this.modalProduct, this.modalVariantId);
        this.dialog.close();
      } else if (action === 'add') this.addToCart();
      else if (action === 'jump') this.mount.querySelector('[data-main-gallery]')?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      else if (action.startsWith('gallery-') || action.startsWith('modal-gallery-')) this.changeGallery(action, button);
    }

    changeGallery(action, button) {
      const modal = action.startsWith('modal-');
      const images = this.photos(modal ? this.modalProduct : this.products.get(this.selected.base));
      if (!images.length) return;
      const key = modal ? 'modalIndex' : 'galleryIndex';
      this[key] = action.endsWith('index') ? Number(button.dataset.index) : (this[key] + (action.endsWith('next') ? 1 : -1) + images.length) % images.length;
      if (modal) this.renderModalGallery();
      else this.renderGallery();
      const context = modal ? this.dialog : this.mount;
      context.querySelector(`[data-action="${action}"]${action.endsWith('index') ? `[data-index="${this[key]}"]` : ''}`)?.focus({ preventScroll: true });
    }

    openDetails(product, trigger, index = 0) {
      if (!product || !this.dialog) return;
      this.modalProduct = product;
      this.modalTrigger = trigger;
      this.modalIndex = index;
      this.modalVariantId = this.variantSelections.get(product.id) || (product.variants.length === 1 && product.variants[0].available ? product.variants[0].id : null);
      this.dialog.innerHTML = `<button type="button" class="scs-close" data-action="close" aria-label="${escape(this.text('close'))}">×</button><div class="scs-modal-gallery" data-modal-gallery></div><div class="scs-modal-copy"><h2 id="${this.studioPrefix}-modal-title">${escape(product.title)}</h2><p data-modal-price>${escape(this.productPrice(product))}</p><div class="scs-product-copy rte" data-modal-copy></div></div><div class="scs-modal-footer"><div class="scs-modal-select">${this.variantControl(product, this.modalVariantId, 'modal')}</div><button type="button" class="scs-primary" data-action="modal-choose"></button></div>`;
      this.setRichText(this.dialog.querySelector('[data-modal-copy]'), product.description);
      this.renderModalGallery();
      this.updateModalButton();
      if (!this.dialog.open) this.dialog.showModal();
    }

    renderModalGallery() {
      if (!this.dialog) return;
      const images = this.photos(this.modalProduct);
      this.modalIndex = images.length ? Math.min(this.modalIndex, images.length - 1) : 0;
      this.dialog.querySelector('[data-modal-gallery]').innerHTML = `<div class="scs-modal-stage">${this.imageMarkup(images[this.modalIndex], this.modalProduct.title, true)}</div>${images.length > 1 ? `<div class="scs-gallery-controls"><button type="button" data-action="modal-gallery-prev" aria-label="${escape(this.text('previous'))}">←</button><span>${this.modalIndex + 1} / ${images.length}</span><button type="button" data-action="modal-gallery-next" aria-label="${escape(this.text('next'))}">→</button></div><div class="scs-modal-thumbs">${images.map((image, index) => `<button type="button" class="scs-thumb" data-action="modal-gallery-index" data-index="${index}" aria-label="${escape(`${this.text('gallery')} ${index + 1}`)}" aria-pressed="${index === this.modalIndex}">${this.imageMarkup(image, this.modalProduct.title)}</button>`).join('')}</div>` : ''}`;
    }

    updateModalButton() {
      const product = this.modalProduct;
      if (!product || !this.dialog) return;
      const variant = product.variants.find((item) => item.id === this.modalVariantId);
      const button = this.dialog.querySelector('[data-action="modal-choose"]');
      if (!button) return;
      const selected = this.isSelected(product) && this.variant(product)?.id === variant?.id;
      button.disabled = this.pending || !this.isAvailable(product) || !variant?.available;
      button.textContent = this.text(!this.isAvailable(product) ? 'soldOut' : selected ? 'selected' : 'choose');
      const select = this.dialog.querySelector('[data-variant-context="modal"]');
      if (select) select.disabled = this.pending;
      const price = this.dialog.querySelector('[data-modal-price]');
      price.textContent = variant ? this.money(variant.price) : this.productPrice(product);
      price.classList.toggle('scs-price--sale', this.priceIsOnSale(product, variant));
    }

    setStatus(message, error = false) {
      let status = this.mount?.querySelector('[data-status]');
      if (!status) {
        if (!message) return;
        status = document.createElement('p');
        status.className = 'scs-status';
        status.dataset.status = '';
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('aria-atomic', 'true');
        (this.mount || this).append(status);
      }
      status.textContent = message;
      status.dataset.error = String(error);
      status.setAttribute('role', error ? 'alert' : 'status');
      const cartLink = this.mount?.querySelector('[data-cart-link]');
      if (cartLink) cartLink.hidden = !message;
    }

    async addToCart() {
      if (this.pending || !this.selected.base) return;
      const products = categories.flatMap((category) => this.selectedProducts(category));
      if (!products.every((product) => this.isAvailable(product) && this.variant(product)?.available)) {
        this.setStatus(this.text('selectVariant'), true);
        return;
      }
      const items = products.map((product) => ({ id: this.variant(product).id, quantity: 1 }));
      this.pending = true;
      this.updateSelections();
      this.updateModalButton();
      this.setStatus(this.text('adding'));
      try {
        const endpoint = new URL(this.data.cartAddUrl, window.location.origin);
        if (endpoint.origin !== window.location.origin) throw new Error(this.text('error'));
        if (endpoint.pathname.endsWith('/add')) endpoint.pathname += '.js';
        const response = await fetch(endpoint.href, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        let result;
        try { result = await response.json(); } catch (_) { throw new Error(this.text('error')); }
        if (!response.ok || Number(result.status) >= 400) {
          const detail = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : this.text('error');
          throw new Error(detail);
        }
        const confirmed = Array.isArray(result.items) ? result.items : (result.id ? [result] : []);
        if (!items.every((item) => confirmed.some((line) => Number(line.variant_id || line.id) === item.id && Number(line.quantity) >= item.quantity))) throw new Error(this.text('error'));
        if (!this.isConnected) return;
        if (this.dialog?.open) this.dialog.close();
        this.setStatus(this.text('added'));
        const button = this.mount.querySelector('[data-action="add"]');
        button?.dispatchEvent(new CustomEvent('theme:product:add', { detail: { response: result, button }, bubbles: true }));
        try { await this.refreshCart(result); } catch (_) { this.setStatus(this.text('added')); }
      } catch (error) {
        if (this.isConnected) this.setStatus(error.message || this.text('error'), true);
      } finally {
        this.pending = false;
        if (this.isConnected) {
          this.updateSelections();
          this.updateModalButton();
        }
      }
    }

    async refreshCart() {
      if (document.querySelector('cart-items') && document.querySelector('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
        return;
      }
      const cartUrl = safeUrl(this.data.cartUrl);
      if (cartUrl && new URL(cartUrl).origin === window.location.origin) window.location.assign(cartUrl);
    }
  }

  customElements.define('shinora-chain-studio', ShinoraChainStudio);
})();
